import { sendExtensionMessage } from "@sharpana/auth/api_client/allauth";
import { useIdeasNodeStore } from "../../stores/ideaNodesStore";
import { timeSource } from "../../pages/summary_session_styles";
import { isWebExtension } from "@sharpana/shared/utils/web_environment";
import * as Sentry from "@sentry/react";

export function timeStringToSeconds(timeString: string) {
  // Validate time string format
  if (!/^(\d{1,2}:)?[0-5]?\d:[0-5]\d$/.test(timeString)) {
    // We don't want to log this since we actually expect invalid formats while the strings are being generated by the LLM
    // console.error('Invalid time format:', timeString);
    // Sentry.captureException(new Error(`Invalid time format (was expecting HH:MM:SS or H:MM:SS or MM:SS): ${timeString}`));
    return 0; // Return a default value or handle the error as needed
  }

  const timeParts = timeString.split(":").map(Number);
  let seconds = 0;
  let multiplier = 1;

  // Start from the right (seconds) and work leftwards (minutes, hours)
  while (timeParts.length > 0) {
    const currentPart = timeParts.pop()!;
    seconds += currentPart * multiplier;
    multiplier *= 60; // Increase multiplier (seconds -> minutes -> hours)
  }

  return seconds;
}

function formatTimeString(timeString: string): string {
  // Validate time string format
  if (!/^(\d{1,2}:)?[0-5]?\d:[0-5]\d$/.test(timeString)) {
    // We don't want to log this since we actually expect invalid formats while the strings are being generated by the LLM
    // console.error('Invalid time format:', timeString);
    // Sentry.captureException(new Error(`Invalid time format in formatTimeString: ${timeString}`));
    return timeString; // Return original string instead of crashing
  }

  const parts = timeString.split(':').map(Number);
  
  // Handle different formats (H:MM:SS, HH:MM:SS, or MM:SS)
  const [hours, minutes, seconds] = parts.length === 2 
    ? [0, ...parts] 
    : parts;
  
  // Validate ranges
  if (
    hours < 0 || hours > 99 ||
    minutes < 0 || minutes > 59 ||
    seconds < 0 || seconds > 59
  ) {
    // We don't want to log this since we actually expect invalid formats while the strings are being generated by the LLM
    // console.error('Invalid time values:', timeString);
    // Sentry.captureException(new Error(`Invalid time values in formatTimeString: ${timeString}`));
    return timeString;
  }
  
  // Only show hours if they are non-zero
  if (hours === 0) {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  
  return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export const TimeReference = (props: {
  startTime: string;
  endTime: string;
}) => {
  const startTimeInSeconds = timeStringToSeconds(String(props.startTime));
  const tabId = useIdeasNodeStore((state) => state.tabId);
  const sessionMetadata = useIdeasNodeStore((state) => state.sessionMetadata);

  const webExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;

  const isOnWebExtension = isWebExtension();

  const hoverAtTime = () => {
    if (isOnWebExtension) {
      sendExtensionMessage(webExtensionId, {
        message: "seek_yt_video_to_time",
        tabId: tabId,
        targetTimeInSecs: startTimeInSeconds,
      });
    }
  };

  const mouseOutFromTime = () => {
    if (isOnWebExtension) {
      sendExtensionMessage(webExtensionId, {
        message: "mouse_out_yt_video_from_time",
        tabId: tabId,
        targetTimeInSecs: startTimeInSeconds,
      });
    }
  };

  const playAtTime = () => {
    if (isOnWebExtension) {
      sendExtensionMessage(webExtensionId, {
        message: "play_yt_video_to_time",
        tabId: tabId,
        targetTimeInSecs: startTimeInSeconds,
      });
    } else {
      if (sessionMetadata && sessionMetadata.targetUrl) {
        const newTab = window.open(
          `${sessionMetadata.targetUrl}#t=${startTimeInSeconds}`,
          "_blank",
          "noopener,noreferrer"
        );
        if (newTab) {
          newTab.opener = null;
        }
      }
    }
  };

  return (
    <span
      onMouseOver={hoverAtTime}
      onMouseLeave={mouseOutFromTime}
      onClick={playAtTime}
      css={timeSource}
    >
      {" "}
      {formatTimeString(props.startTime)}-{formatTimeString(props.endTime)}
    </span>
  );
};
